import React, { useState } from 'react';
import { CheckCircle, Send } from 'lucide-react';
import type { QuizTier } from './QuizShell';

// Pillar page links per niche (fallback if niches.ts not available)
const NICHE_PILLAR: Record<string, { label: string; href: string }> = {
  'stress-symptoms': {
    label: 'Learn more about stress-related symptoms →',
    href: '/stress-physical-symptoms',
  },
  'medical-anxiety': {
    label: 'Learn more about medical anxiety →',
    href: '/medical-anxiety-hypnotherapy',
  },
  'bladder-urgency': {
    label: 'Learn more about bladder urgency hypnotherapy →',
    href: '/bladder-urgency-hypnotherapy',
  },
};

interface QuizResultProps {
  tier: QuizTier;
  niche: string;
  quizSlug: string;
  onSubmit: (formData: {
    name: string;
    email: string;
    consentMarketing: boolean;
    consentHealth: boolean;
  }) => Promise<void>;
  isSubmitting: boolean;
  isComplete: boolean;
}

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const QuizResult: React.FC<QuizResultProps> = ({
  tier,
  niche,
  onSubmit,
  isSubmitting,
  isComplete,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentHealth, setConsentHealth] = useState(false);
  const [emailError, setEmailError] = useState('');

  const firstName = name.split(' ')[0] || name;
  const pillar = NICHE_PILLAR[niche];
  const canSubmit =
    name.trim().length > 0 &&
    isValidEmail(email) &&
    consentMarketing &&
    consentHealth;

  const handleEmailBlur = () => {
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), email: email.trim(), consentMarketing, consentHealth });
  };

  if (isComplete) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-12 h-12 text-accent-600 mx-auto" />
        <h2 className="text-xl font-heading font-bold text-gray-900 mt-4 mb-2">
          Thank you, {firstName}
        </h2>
        <p className="text-gray-600 leading-relaxed mb-6">
          We'll be in touch with your personalised plan shortly. Check your inbox.
        </p>
        {pillar && (
          <a
            href={pillar.href}
            className="text-accent-600 hover:text-accent-700 font-bold underline underline-offset-2"
          >
            {pillar.label}
          </a>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Tier result */}
      <p className="text-[28px] font-heading font-bold text-accent-600 mb-2">
        {tier.name}
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-6">{tier.blurb}</p>

      <hr className="border-t border-gray-200 my-6" />

      {/* Email capture form */}
      <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">
        Get your personalised plan
      </h2>

      <form onSubmit={handleSubmit} noValidate>
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-accent-600 focus:ring-1 focus:ring-accent-600 outline-none text-gray-900"
        />

        <input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={handleEmailBlur}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-accent-600 focus:ring-1 focus:ring-accent-600 outline-none text-gray-900 mt-3"
        />
        {emailError && (
          <p className="text-red-500 text-sm mt-1">{emailError}</p>
        )}

        {/* Marketing consent */}
        <div className="flex items-start space-x-3 mt-5">
          <input
            id="consent-marketing"
            type="checkbox"
            checked={consentMarketing}
            onChange={(e) => setConsentMarketing(e.target.checked)}
            className="mt-0.5 w-4 h-4 cursor-pointer"
          />
          <label
            htmlFor="consent-marketing"
            className="text-sm text-gray-600 leading-relaxed cursor-pointer"
          >
            I agree to receive email updates and information from Sharon Clare
            Hypnotherapy. You can unsubscribe at any time.
          </label>
        </div>

        {/* Health data consent */}
        <div className="flex items-start space-x-3 mt-3">
          <input
            id="consent-health"
            type="checkbox"
            checked={consentHealth}
            onChange={(e) => setConsentHealth(e.target.checked)}
            className="mt-0.5 w-4 h-4 cursor-pointer"
          />
          <label
            htmlFor="consent-health"
            className="text-sm text-gray-600 leading-relaxed cursor-pointer"
          >
            I consent to Sharon Clare Hypnotherapy storing my quiz responses,
            which may include health-related information, for up to 12 months.
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!canSubmit || isSubmitting}
          className={`mt-6 w-full py-4 rounded-xl font-bold text-base flex items-center justify-center space-x-2 transition-colors ${
            canSubmit && !isSubmitting
              ? 'bg-accent-600 text-white hover:bg-accent-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <>
              <span
                className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"
                aria-hidden="true"
              />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Send me my personalised plan</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default QuizResult;
